/* eslint-disable max-lines */
import { ProjectOutlined } from '@ant-design/icons/lib'
import { Row } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import DamageInfoCard from 'app/components/Core/DamageInfo/Card'
import QuoteCard from 'app/components/Core/Quote/Card'
import UpdateCard from 'app/components/Core/Rental/Card/Update'
import CopyRequestTextInfo from 'app/components/Core/Rental/CopyTextInfo'
import Documents from 'app/components/Core/Rental/Documents'
import StatusInfo from 'app/components/Core/Rental/StatusInfo'
import StatusInfoSteps from 'app/components/Core/Rental/StatusInfoSteps'
import PullToRefresh from 'app/components/UI-Elements/General/PullToRefresh'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RentalSuccessStatus } from 'app/constants/Enums'
import { RecordType } from 'app/constants/GlobalTypes'
import { normalizeNestedStructure } from 'app/lib/apollo/normalizers/LineItem'
import rentalNormalizer from 'app/lib/apollo/normalizers/Rental'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useCurrentContactOrganization } from 'app/lib/hooks/useCurrent'
import { useQuery } from 'app/lib/hooks/useQuery'
import useSetBackHistory from 'app/lib/hooks/useSetBackHistory'
import useTitle from 'app/lib/hooks/useTitle'
import useTranslation from 'app/lib/hooks/useTranslation'
import { notify } from 'app/lib/Notify'
import { getQueryString } from 'app/lib/utils/search-params'
import RentalOverview from 'app/pages/Rental/Components/RentalOverview'
import Deliveries from 'app/pages/Rental/Containers/Deliveries'
import { PageProps } from 'app/pages/typing'
import * as rentals from 'app/queries/Rentals'
import { Routes } from 'app/routes/Routes'
import React, { ReactNode, useEffect } from 'react'
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'

import Products from '../Containers/Products'

const RentalsShow: React.FC<PageProps> = (props) => {
  const { titlelizer } = useTitle(props)
  const { md } = useBreakpoint()
  const { setBackRoute } = useBrowserRouterContext()
  const { rental_id } = useParams<{ rental_id: string }>()
  const navigate = useNavigate()
  const { role } = useCurrentContactOrganization()
  const { tRoute } = useTranslation({ scope: props.route.key })
  const { search, pathname } = useLocation()
  const match = useMatch(Routes.Rental.member().show)
  const { generateId } = ToolBox.useId()
  const { data, loading, refetch } = useQuery<{ rental: IRental }>(rentals.RENTAL_SHOW_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      id: rental_id,
      omitStatusWhenCanceled: true
    },
    normalizer: (data) => ({
      rental: {
        ...rentalNormalizer(data.rental),
        products: normalizeNestedStructure(data.rental?.productsWithSubItems || [])
      }
    })
  })
  const getProjectLinkId = generateId({
    recordType: RecordType.rental,
    componentType: 'link',
    id: 'to_project_page'
  })

  const rental = data?.rental
  const success = getQueryString(search, 'success') as RentalSuccessStatus

  useEffect(() => {
    if (match && role === 'guest') {
      navigate(Routes.Error.collection().role_not_authorized, { replace: true })
    }
  }, [])

  useEffect(() => {
    refetch()
  }, [pathname])

  useSetBackHistory(() => setBackRoute(Routes.Rental.collection().index))

  useEffect(() => {
    switch (success) {
      case RentalSuccessStatus.created:
        showSuccess({
          message: tRoute({ id: 'created_title' }),
          description: notificationContent()
        })
        break
      case RentalSuccessStatus.accepted:
        showSuccess({
          message: tRoute({ id: 'accepted_title' }),
          description: tRoute({ id: 'accepted_content' })
        })
        break
      case RentalSuccessStatus.paid:
        showSuccess({
          message: tRoute({ id: 'paid_title' }),
          description: tRoute({ id: 'accepted_content' })
        })
        break
      case RentalSuccessStatus.booked:
        showSuccess({ message: tRoute({ id: 'booked_message' }) })
        break
    }
  }, [])

  const showSuccess = (options: Partial<Record<'message' | 'description', ReactNode>>) => {
    notify({
      intent: 'success',
      position: 'topRight',
      className: 'success-notification',
      message: options.message,
      additionalOptions: { top: 90, description: options.description }
    })
  }

  const notificationContent = (): React.ReactNode => {
    if (role === 'member') {
      return tRoute(
        {
          id: 'success_content_member'
        },
        { approver: data?.rental?.managerContact?.name }
      )
    } else {
      return tRoute({ id: 'success_content_manager' })
    }
  }

  return (
    <PullToRefresh onRefresh={refetch}>
      <Row gutter={[{ xs: 0, md: 12 }, 0]}>
        <KCol xs={24} lg={16}>
          <Row gutter={[{ xs: 0, md: 12 }, 12]}>
            <KCol card span={24} loading={loading}>
              <Row gutter={[{ xs: 0, md: 12 }, 12]}>
                <KCol loading={loading} xs={0} sm={0} md={0} lg={24}>
                  <h3>{titlelizer({ rental_code: rental?.rental_code })}</h3>
                </KCol>
                <KCol loading={loading} span={24}>
                  <If condition={rental}>
                    <h5 className="text-black mb-0">
                      <Link
                        id={getProjectLinkId}
                        to={Routes.Project.member(data.rental?.project?.id).show}>
                        <div className="d-flex">
                          <ProjectOutlined className="my-auto mr-2 text-xl" />
                          {`${data.rental?.project?.name} - ${data.rental?.contact?.name}`}
                        </div>
                      </Link>
                    </h5>
                  </If>
                </KCol>
                <KCol loading={loading} span={24}>
                  <If condition={rental}>
                    <StatusInfo member={rental} />
                  </If>
                  <If condition={rental?.status === 'quote_in_preparation' && md}>
                    <CopyRequestTextInfo member={rental} />
                  </If>
                </KCol>
                <KCol loading={loading} span={24}>
                  <If condition={rental}>
                    <RentalOverview rental={rental} />
                  </If>
                </KCol>
              </Row>
            </KCol>
            <KCol loading={loading} card={loading} span={24}>
              <Products rental={rental} />
            </KCol>
          </Row>
        </KCol>
        <KCol xs={24} lg={8}>
          <Row gutter={[0, 12]}>
            <If condition={rental && rental.status_info.stepper_data?.steps?.length}>
              <KCol
                card
                title={tRoute({ id: 'status_overview' })}
                loading={loading}
                span={0}
                lg={24}>
                <StatusInfoSteps rental={rental} />
              </KCol>
            </If>
            <If condition={rental?.lastQuote?.valid}>
              <KCol span={24}>
                <QuoteCard quote={rental.lastQuote} />
              </KCol>
            </If>
            <If condition={rental?.status === 'quote_in_preparation'}>
              <UpdateCard member={rental} props={{ loading }} />
            </If>
            <KCol card title={tRoute({ id: 'documents' })} loading={loading} span={0} lg={24}>
              <If condition={rental}>
                <Documents member={rental} />
              </If>
            </KCol>
            <DamageInfoCard rental={rental} />
            <KCol span={24} title={tRoute({ id: 'deliveries' })} loading={loading} card>
              <If condition={rental}>
                <Deliveries rental={rental} />
              </If>
            </KCol>
          </Row>
        </KCol>
      </Row>
    </PullToRefresh>
  )
}

export default RentalsShow
